import React from "react"
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useTheme
} from "@mui/material"
import Button from "components/Button"
import ResponsiveAccordion from "components/ResponsiveAccordion/ResponsiveAccordion"
import { useTranslation } from "react-i18next"
import { IApiResult } from "auth/interface/api.interface"

interface ConfirmDepartmentRemoveModalProps {
  conflictDialogOpen: boolean
  closeConflictDialog: () => void
  confirmAndSave: () => void
  checkConflictsResult: IApiResult
}

const ConfirmDepartmentRemoveModal = ({
  conflictDialogOpen,
  closeConflictDialog,
  confirmAndSave,
  checkConflictsResult
}: ConfirmDepartmentRemoveModalProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const grayBg = theme.palette.grey[100]
  const accordionCss = {
    backgroundColor: theme.palette.background.paper,
    marginBottom: 4,
    "& .MuiTypography-h4": {
      fontSize: theme.typography.body1.fontSize,
      fontWeight: theme.typography.fontWeightMedium
    }
  } as React.CSSProperties

  return (
    <Dialog open={conflictDialogOpen} onClose={closeConflictDialog} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: grayBg, typography: "bodyLarge", fontWeight: theme.typography.fontWeightBold }}>
        {t("settings.companies.department_has_connections", "Departments with active connections")}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: grayBg }}>
        <DialogContentText mb={2}>
          {t(
            "settings.companies.department_connections_warning",
            "The following departments have active connections that will be removed. Do you want to continue?"
          )}
        </DialogContentText>
        {(checkConflictsResult.data ?? []).map((conflict: any) => (
          <Box key={conflict.departmentId} mt={2}>
            <Typography variant="h6" mb={1}>
              <strong>{conflict.departmentName}</strong>
            </Typography>
            {conflict.users.length > 0 && (
              <ResponsiveAccordion
                title={`${t("global.users", "Users")} (${conflict.users.length})`}
                customCss={accordionCss}
              >
                <Box display="flex" flexDirection="column" gap={0.5} px={2} pb={1}>
                  {conflict.users.map((user: any) => (
                    <Typography key={user.userId} variant="bodySmall">
                      {user.name} · {user.email}
                    </Typography>
                  ))}
                </Box>
              </ResponsiveAccordion>
            )}
            {conflict.articles.length > 0 && (
              <ResponsiveAccordion
                title={`${t("global.news", "News")} (${conflict.articles.length})`}
                customCss={accordionCss}
              >
                <Box display="flex" flexDirection="column" gap={0.5} px={2} pb={1}>
                  {conflict.articles.map((article: any) => (
                    <Typography key={article.articleId} variant="bodySmall">
                      {article.subject}
                    </Typography>
                  ))}
                </Box>
              </ResponsiveAccordion>
            )}
            {conflict.documents.length > 0 && (
              <ResponsiveAccordion
                title={`${t("global.documents", "Documents")} (${conflict.documents.length})`}
                customCss={accordionCss}
              >
                <Box display="flex" flexDirection="column" gap={0.5} px={2} pb={1}>
                  {conflict.documents.map((document: any) => (
                    <Typography key={document.documentId} variant="bodySmall">
                      {document.name}
                    </Typography>
                  ))}
                </Box>
              </ResponsiveAccordion>
            )}
            {conflict.vehicles.length > 0 && (
              <ResponsiveAccordion
                title={`${t("global.vehicles", "Vehicles")} (${conflict.vehicles.length})`}
                customCss={accordionCss}
              >
                <Box display="flex" flexDirection="column" gap={0.5} px={2} pb={1}>
                  {conflict.vehicles.map((vehicle: any) => (
                    <Typography key={vehicle.vehicleId} variant="bodySmall">
                      {vehicle.regNumber || vehicle.internNumber}
                    </Typography>
                  ))}
                </Box>
              </ResponsiveAccordion>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ py: 2, px: 3, bgcolor: grayBg }}>
        <Button onClick={closeConflictDialog} variant="outlined">
          {t("global.cancel", "Cancel")}
        </Button>
        <Button onClick={confirmAndSave} color="primary">
          {t("global.delete_and_save", "Delete and save")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmDepartmentRemoveModal
